/**
 * iter303 — Phase 2 de la régularisation TDFN : la TVA encaissée directement en banque.
 *
 * L'ancienne comptabilité portait parfois la TVA 8.1 % au 2200 depuis le relevé bancaire,
 * sans facture. Chaque écriture est rapprochée de la vente de la même pièce ; seules les
 * écritures certaines sont reclassées, les autres sont listées et laissées intactes.
 */
import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Landmark, Undo2, Play, SearchCheck, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmt = (n) => (n || 0).toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => String(d || "").slice(0, 10).split("-").reverse().join(".");

const Ligne = ({ label, avant, apres }) => (
  <tr className="border-t border-gray-100">
    <td className="px-3 py-1.5 text-gray-700">{label}</td>
    <td className="px-3 py-1.5 text-right font-mono text-xs text-gray-500">{fmt(avant)}</td>
    <td className="px-3 py-1.5 text-right font-mono text-xs font-semibold">{fmt(apres)}</td>
  </tr>
);

const TdfnEncaissementsCard = ({ role, onDone }) => {
  const [rappro, setRappro] = useState(null);
  const [sim, setSim] = useState(null);
  const [detail, setDetail] = useState(false);
  const [migration, setMigration] = useState(null);
  const [busy, setBusy] = useState(false);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/tva/migration");
      setMigration((data || []).find((m) => !m.annule && m.type === "encaissements_bancaires") || null);
    } catch { /* lecture seule */ }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const analyser = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/tva/migration/encaissements/rapprochement");
      setRappro(data);
      const { data: s } = await api.post("/tva/migration/encaissements/simulation");
      setSim(s);
      toast.success(`${data.nb_confirmees} écriture(s) rapprochée(s) — rien n'a été écrit`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const executer = async () => {
    if (!window.confirm(
      `Reclasser ${fmt(rappro.total_confirme)} CHF de TVA encaissée ?\n\n`
      + `${rappro.nb_confirmees} écriture(s) rapprochée(s) avec certitude · `
      + `${rappro.nb_non_confirmees} laissée(s) de côté.\n`
      + `Solde 2200 : ${fmt(sim.avant.solde_2200)} → ${fmt(sim.apres.solde_2200)}\n\n`
      + "Aucune écriture existante n'est modifiée. Annulable à tout moment.",
    )) return;
    setBusy(true);
    try {
      await api.post("/tva/migration/encaissements/executer", { confirmer: true });
      toast.success("Encaissements reclassés");
      setSim(null); setRappro(null);
      charger(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const annuler = async () => {
    if (!window.confirm("Annuler le reclassement des encaissements ?\n\nLes écritures ajoutées seront supprimées et les soldes reviendront à leur état précédent.")) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/tva/migration/${migration.id}?confirmer=true`);
      toast.success(`${data.ecritures_supprimees} écriture(s) supprimée(s)`);
      charger(); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  if (role !== "admin") return null;

  return (
    <Card className="p-4" data-testid="tdfn-encaissements-card">
      <div className="flex items-center gap-2 mb-1">
        <Landmark className="w-4 h-4 text-[#52a675]" />
        <h3 className="text-base font-semibold text-gray-900">TVA encaissée directement en banque</h3>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Écritures d&apos;import qui créditaient le 2200 depuis le relevé bancaire, sans facture.
        Chacune est rapprochée de la vente de la même pièce : contrepartie de trésorerie, produit
        3xxx déjà au chiffre d&apos;affaires, TVA égale à 8.1 % de ce produit. Les écritures qui
        ne remplissent pas tous les critères ne sont pas reclassées.
      </p>

      {migration ? (
        <div className="p-3 rounded-lg border border-[#52a675]/40 bg-[#52a675]/5 text-xs" data-testid="tdfn-encaissements-active">
          <div className="font-semibold text-gray-900">
            Reclassement exécuté le {fmtDate(migration.execute_le)}
            {migration.execute_par_nom ? ` par ${migration.execute_par_nom}` : ""}
          </div>
          <div className="text-gray-600 mt-0.5">
            {migration.ecritures_rapprochees?.length} écriture(s) rapprochée(s) ·{" "}
            {migration.ecritures_creees?.length} écriture(s) de régularisation ajoutée(s) · solde 2200 :{" "}
            {fmt(migration.situation_apres_reelle?.solde_2200)} CHF
          </div>
          {migration.ecritures_non_rapprochees?.length > 0 && (
            <div className="text-amber-700 mt-0.5">
              {migration.ecritures_non_rapprochees.length} écriture(s) non identifiée(s), laissée(s) au 2200
            </div>
          )}
          <Button size="sm" variant="outline" className="mt-2" disabled={busy} onClick={annuler} data-testid="tdfn-encaissements-annuler">
            <Undo2 className="w-3.5 h-3.5 mr-1.5" /> Annuler ce reclassement
          </Button>
        </div>
      ) : (
        <>
          <Button size="sm" variant="outline" onClick={analyser} disabled={busy} data-testid="tdfn-encaissements-analyser">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <SearchCheck className="w-4 h-4 mr-1.5" />}
            Analyser et rapprocher (rien n&apos;est écrit)
          </Button>

          {rappro && sim && (
            <div className="mt-3 space-y-2" data-testid="tdfn-encaissements-rapprochement">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-1 rounded-md bg-[#52a675]/10 text-[#2f6b47] font-semibold" data-testid="tdfn-enc-confirmees">
                  {rappro.nb_confirmees} rapprochée(s) · {fmt(rappro.total_confirme)} CHF
                </span>
                <span className={`px-2 py-1 rounded-md font-semibold ${rappro.nb_non_confirmees ? "bg-amber-100 text-amber-800" : "bg-gray-100 text-gray-500"}`} data-testid="tdfn-enc-non-confirmees">
                  {rappro.nb_non_confirmees} non identifiée(s) · {fmt(rappro.total_non_confirme)} CHF
                </span>
                <span className="px-2 py-1 rounded-md bg-gray-100 text-gray-600">
                  {rappro.nb_analysees} écriture(s) analysée(s)
                </span>
              </div>

              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-wider text-gray-500">
                    <th className="px-3 pb-1 text-left font-medium"></th>
                    <th className="px-3 pb-1 text-right font-medium">Avant</th>
                    <th className="px-3 pb-1 text-right font-medium">Après</th>
                  </tr>
                </thead>
                <tbody>
                  <Ligne label="Chiffre d'affaires" avant={sim.avant.ca_produits} apres={sim.apres.ca_produits} />
                  <Ligne label="Résultat" avant={sim.avant.resultat} apres={sim.apres.resultat} />
                  <Ligne label="Solde 2200 (dette TVA)" avant={sim.avant.solde_2200} apres={sim.apres.solde_2200} />
                  <Ligne label="Total actif (bilan)" avant={sim.avant.total_actif} apres={sim.apres.total_actif} />
                  <Ligne label="Écritures au journal" avant={sim.avant.ecritures} apres={sim.apres.ecritures} />
                </tbody>
              </table>

              <button type="button" onClick={() => setDetail(!detail)} className="text-xs text-[#52a675] hover:underline flex items-center gap-1 px-3" data-testid="tdfn-enc-toggle-detail">
                {detail ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                {detail ? "Masquer" : "Voir"} le détail écriture par écriture
              </button>

              {detail && (
                <div className="max-h-80 overflow-auto rounded-lg border border-gray-200" data-testid="tdfn-enc-detail">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-500">
                        <th className="px-2 py-1.5 text-left font-medium">Date</th>
                        <th className="px-2 py-1.5 text-left font-medium">Pièce</th>
                        <th className="px-2 py-1.5 text-right font-medium">TVA</th>
                        <th className="px-2 py-1.5 text-right font-medium">Vente HT</th>
                        <th className="px-2 py-1.5 text-right font-medium">Taux</th>
                        <th className="px-2 py-1.5 text-left font-medium">Comptes</th>
                        <th className="px-2 py-1.5 text-left font-medium">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...rappro.confirmees.map((r) => ({ ...r, ok: true })),
                        ...rappro.non_confirmees.map((r) => ({ ...r, ok: false }))].map((r) => (
                          <tr key={r.id} className="border-t border-gray-100">
                            <td className="px-2 py-1 font-mono">{fmtDate(r.date)}</td>
                            <td className="px-2 py-1 text-gray-600">
                              {r.reference}
                              {r.libelle ? <span className="text-gray-400"> · {r.libelle}</span> : null}
                            </td>
                            <td className="px-2 py-1 text-right font-mono">{fmt(r.montant_tva)}</td>
                            <td className="px-2 py-1 text-right font-mono text-gray-500">{fmt(r.produit_ht)}</td>
                            <td className="px-2 py-1 text-right font-mono text-gray-500">
                              {r.taux_constate != null ? `${r.taux_constate.toFixed(2)} %` : "—"}
                            </td>
                            <td className="px-2 py-1 font-mono text-gray-500">
                              {(r.comptes_produit || []).join(", ") || "—"} / {r.contrepartie}
                            </td>
                            <td className={`px-2 py-1 ${r.ok ? "text-[#2f6b47]" : "text-amber-700"}`}>
                              {r.ok ? "Rapprochée" : (r.motifs || []).join(" · ")}
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="text-xs text-gray-400 px-3">
                Décomptes de contrôle (identiques après) :{" "}
                {Object.entries(sim.decomptes_tva_controle || {}).map(([a, v]) => `${a} : ${fmt(v.tva_due)}`).join(" · ")}
              </div>

              {rappro.nb_confirmees > 0 && (
                <Button size="sm" onClick={executer} disabled={busy} className="bg-[#52a675] hover:bg-[#3d8a5c] ml-3" data-testid="tdfn-encaissements-executer">
                  <Play className="w-3.5 h-3.5 mr-1.5" /> Reclasser les {rappro.nb_confirmees} écritures rapprochées
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default TdfnEncaissementsCard;
